import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { cn } from '@/lib/utils';
import { addDaysIsoDate, todayIsoDate } from '@/lib/formatters';

/**
 * Dropdown calendar for a `YYYY-MM-DD` value.
 *
 * Dates are local calendar days — the same shape as Postgres `date`
 * columns (`tasks.due_date` and friends). Parsing goes through year /
 * month / day parts so a value never shifts a day across timezones.
 */

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const TRIGGER_LABEL = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});
const TRIGGER_LABEL_YEAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const DAY_LABEL = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

function toKey(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function parseKey(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function formatTrigger(value: string): string {
  const date = parseKey(value);
  if (!date) return value;
  return date.getFullYear() === new Date().getFullYear()
    ? TRIGGER_LABEL.format(date)
    : TRIGGER_LABEL_YEAR.format(date);
}

function monthWeeks(view: Date): Date[][] {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const weeks: Date[][] = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w += 1) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d += 1) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    const touchesMonth = week.some((day) => day.getMonth() === view.getMonth());
    if (touchesMonth) weeks.push(week);
  }
  return weeks;
}

function yearChoices(viewYear: number, range: { past: number; future: number }): number[] {
  const now = new Date().getFullYear();
  const start = Math.min(now - range.past, viewYear);
  const end = Math.max(now + range.future, viewYear);
  const years: number[] = [];
  for (let year = start; year <= end; year += 1) years.push(year);
  return years;
}

export interface DatePickerProps {
  /** `YYYY-MM-DD`, or `''` when unset. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  'aria-label'?: string;
  /** Today / Tomorrow / Next week chips inside the calendar. */
  showShortcuts?: boolean;
  /** Years offered in the year menu, relative to this year. */
  yearRange?: { past: number; future: number };
}

const DEFAULT_YEAR_RANGE = { past: 2, future: 6 };

export function DatePicker({
  value,
  onChange,
  disabled = false,
  placeholder = 'Due date',
  className,
  id,
  'aria-label': ariaLabel = 'Due date',
  showShortcuts = true,
  yearRange = DEFAULT_YEAR_RANGE,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => parseKey(value) ?? new Date());
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const dayRefs = useRef(new Map<string, HTMLButtonElement>());
  const labelId = useId();
  const today = todayIsoDate();

  const selected = parseKey(value);
  const weeks = monthWeeks(view);

  useEffect(() => {
    if (!open || !focusKey) return;
    dayRefs.current.get(focusKey)?.focus();
  }, [open, focusKey, view]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;
    const base = parseKey(value) ?? new Date();
    setView(new Date(base.getFullYear(), base.getMonth(), 1));
    setFocusKey(toKey(base));
  };

  const pick = (key: string) => {
    onChange(key);
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setOpen(false);
  };

  const moveView = (year: number, month: number) => {
    const normalized = new Date(year, month, 1);
    const current = parseKey(focusKey);
    const day = current?.getDate() ?? selected?.getDate() ?? 1;
    const dim = new Date(normalized.getFullYear(), normalized.getMonth() + 1, 0).getDate();
    const next = new Date(normalized.getFullYear(), normalized.getMonth(), Math.min(day, dim));
    setView(normalized);
    setFocusKey(toKey(next));
  };

  const onGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const step = delta[event.key];
    if (step != null) {
      event.preventDefault();
      const base = parseKey(focusKey) ?? selected ?? new Date();
      const next = new Date(base);
      next.setDate(next.getDate() + step);
      setView(new Date(next.getFullYear(), next.getMonth(), 1));
      setFocusKey(toKey(next));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      if (!focusKey) return;
      event.preventDefault();
      pick(focusKey);
    }
  };

  const shortcuts = [
    { label: 'Today', value: today },
    { label: 'Tomorrow', value: addDaysIsoDate(today, 1) },
    { label: 'Next week', value: addDaysIsoDate(today, 7) },
  ];

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={selected ? `${ariaLabel}, ${DAY_LABEL.format(selected)}` : ariaLabel}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'flex h-10 w-full min-w-0 items-center gap-2 rounded-lg border border-input bg-card px-3 text-left text-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            selected ? 'text-foreground' : 'text-muted-foreground',
            className,
          )}
        >
          <CalendarDays className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
          <span className="min-w-0 flex-1 truncate">
            {selected ? formatTrigger(value) : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[19rem] p-3"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="space-y-2">
          {showShortcuts && (
            <div className="flex flex-wrap gap-1.5">
              {shortcuts.map((shortcut) => {
                const active = value === shortcut.value;
                return (
                  <button
                    key={shortcut.label}
                    type="button"
                    onClick={() => pick(shortcut.value)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:bg-secondary',
                    )}
                  >
                    {shortcut.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => moveView(view.getFullYear(), view.getMonth() - 1)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <label className="sr-only" htmlFor={`${labelId}-month`}>
              Month
            </label>
            <select
              id={`${labelId}-month`}
              aria-label="Month"
              value={view.getMonth()}
              onChange={(event) => moveView(view.getFullYear(), Number(event.target.value))}
              className="h-8 min-w-0 flex-1 rounded-md border border-input bg-card px-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {MONTHS.map((month, index) => (
                <option key={month} value={index}>
                  {month}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor={`${labelId}-year`}>
              Year
            </label>
            <select
              id={`${labelId}-year`}
              aria-label="Year"
              value={view.getFullYear()}
              onChange={(event) => moveView(Number(event.target.value), view.getMonth())}
              className="h-8 w-[4.75rem] shrink-0 rounded-md border border-input bg-card px-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {yearChoices(view.getFullYear(), yearRange).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => moveView(view.getFullYear(), view.getMonth() + 1)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div
            role="grid"
            aria-label={new Intl.DateTimeFormat('en-US', {
              month: 'long',
              year: 'numeric',
            }).format(view)}
            onKeyDown={onGridKeyDown}
          >
            <div role="row" className="grid grid-cols-7">
              {WEEKDAYS.map((day) => (
                <div
                  key={day}
                  role="columnheader"
                  className="flex h-8 items-center justify-center text-[11px] font-medium text-muted-foreground"
                >
                  {day}
                </div>
              ))}
            </div>
            {weeks.map((week) => (
              <div role="row" key={toKey(week[0]!)} className="grid grid-cols-7">
                {week.map((day) => {
                  const key = toKey(day);
                  const inMonth = day.getMonth() === view.getMonth();
                  const isSelected = key === value;
                  const isToday = key === today;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="gridcell"
                      ref={(node) => {
                        if (node) dayRefs.current.set(key, node);
                        else dayRefs.current.delete(key);
                      }}
                      tabIndex={key === focusKey ? 0 : -1}
                      aria-selected={isSelected}
                      aria-current={isToday ? 'date' : undefined}
                      aria-label={DAY_LABEL.format(day)}
                      onClick={() => pick(key)}
                      className={cn(
                        'mx-auto flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isSelected
                          ? 'bg-primary font-medium text-primary-foreground'
                          : inMonth
                            ? 'text-foreground hover:bg-secondary'
                            : 'text-muted-foreground/50 hover:bg-secondary',
                        isToday && !isSelected && 'ring-1 ring-primary',
                      )}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-border pt-2">
            <button
              type="button"
              onClick={clear}
              disabled={!value}
              className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => pick(today)}
              className="rounded-md px-2 py-1 text-xs font-medium text-foreground hover:bg-secondary"
            >
              Today
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
